// src/auth/useGraph.js
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { persistMicrosoftTenantId } from "./msalConfig";

/** Normalize Graph URL */
function normalizeGraphUrl(url) {
  if (!url) throw new Error("Graph URL is required");

  const trimmed = url.trim();

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  return `https://graph.microsoft.com/v1.0${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
}

/** Infer sensible default scopes */
function inferScopesForUrl(url) {
  const u = url.toLowerCase();

  if (
    u.includes("/mail") ||
    u.includes("/messages") ||
    u.includes("/sendmail")
  ) {
    return [
      "User.Read",
      "offline_access",
      "Mail.Read",
      "Mail.ReadWrite",
      "Mail.Send",
    ];
  }

  if (u.includes("/calendars") || u.includes("/events")) {
    return [
      "User.Read",
      "offline_access",
      "Calendars.Read",
      "Calendars.ReadWrite",
    ];
  }

  return ["User.Read", "offline_access"];
}

export function useGraph() {
  const { instance } = useMsal();

  // --------------------------------------------------
  // Token Helper
  // --------------------------------------------------
  async function getToken(scopes) {
    const account =
      instance.getActiveAccount() || instance.getAllAccounts()[0] || null;

    if (!account) {
      throw new Error("No logged-in Microsoft account found");
    }

    const request = {
      scopes:
        Array.isArray(scopes) && scopes.length
          ? scopes
          : ["User.Read", "offline_access"],
      account,
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      persistMicrosoftTenantId(response);
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(request);
        persistMicrosoftTenantId(response);
        return response.accessToken;
      }

      throw err;
    }
  }

  // --------------------------------------------------
  // Core request wrapper
  // --------------------------------------------------
  async function request(method, url, body = undefined, scopes = undefined) {
    const endpoint = normalizeGraphUrl(url);

    const neededScopes =
      scopes && scopes.length ? scopes : inferScopesForUrl(endpoint);

    const token = await getToken(neededScopes);

    const res = await fetch(endpoint, {
      method,
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body:
        body !== undefined
          ? typeof body === "string"
            ? body
            : JSON.stringify(body)
          : undefined,
    });

    const contentType = res.headers.get("content-type") || "";

    // --------------------------------------------------
    // Error handling
    // --------------------------------------------------
    if (!res.ok) {
      let errText = "";

      try {
        errText = await res.clone().text();
      } catch {
        // ignore
      }

      let errJson = null;

      try {
        if (contentType.includes("application/json")) {
          errJson = await res.json();
        } else if (errText) {
          try {
            errJson = JSON.parse(errText);
          } catch {
            // not JSON
          }
        }
      } catch {
        // ignore
      }

      const message =
        errJson?.error?.message ||
        errJson?.message ||
        errText ||
        `HTTP ${res.status} ${res.statusText}`;

      const error = new Error(message);

      error.status = res.status;
      error.endpoint = endpoint;
      error.error = errJson || errText || null;

      throw error;
    }

    // --------------------------------------------------
    // No content responses
    // --------------------------------------------------
    if (res.status === 202 || res.status === 204) return {};

    // --------------------------------------------------
    // Success parsing
    // --------------------------------------------------
    if (contentType.includes("application/json")) {
      return await res.json();
    }

    if (contentType.startsWith("text/") || contentType.includes("html")) {
      return await res.text();
    }

    return await res.blob();
  }

  // --------------------------------------------------
  // Public helpers
  // --------------------------------------------------
  async function graphGet(url, scopes) {
    return request("GET", url, undefined, scopes);
  }

  // Supports POST or PATCH override
  async function graphPost(url, body, methodOrScopes, maybeScopes) {
    const isMethod = typeof methodOrScopes === "string";

    const method = isMethod ? methodOrScopes : "POST";

    const scopes = isMethod ? maybeScopes : methodOrScopes;

    return request(method, url, body, scopes);
  }

  async function graphPatch(url, body, scopes) {
    return request("PATCH", url, body, scopes);
  }

  return {
    getToken,
    graphGet,
    graphPost,
    graphPatch,
  };
}
